import { satisfies, validRange, lt as semverLessThan } from 'semver';
import { z } from 'zod';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { logger } from '~/utils/logger';

/**
 * This modules defines for some packages
 * versions that are known to have a problem
 * and that should be updated automatically.
 *
 * We do an `npm upgrade` when this is semver compatible with
 * the version specified in the package.json.
 *
 * If this fails we send an analytic event for now with the idea of
 * doing an `npm install package@<minimum-version>` the future.
 */

interface UpgradeSpec {
  /**
   * A version range that the installed version (version present in the lock file)
   * must match in order for the upgrade spec to maybe apply.
   */
  versionRange: string;

  /**
   * Minimum version that should be installed.
   */
  minimumVersion: string;
}

const packagesSpec: Record<string, UpgradeSpec[]> = {
  tailwindcss: [
    {
      versionRange: '>=3 <4',
      minimumVersion: '3.4.17',
    },
  ],
};

export function checkForUpgrades(packageJsonContent: string, packageLockContent: string): string[] {
  try {
    const packageJson = packageJsonSchema.parse(JSON.parse(packageJsonContent));
    const packageLock = packageLockSchema.parse(JSON.parse(packageLockContent));

    const upgrades = [];

    for (const [packageName, upgradeSpecs] of Object.entries(packagesSpec)) {
      const currentVersion = getSpecifiedVersion(packageJson, packageName);

      // package is not specified or is a github dependency
      if (!currentVersion) {
        continue;
      }

      const installedVersion = packageLock.packages[`node_modules/${packageName}`]?.version;

      for (const { versionRange, minimumVersion } of upgradeSpecs) {
        /**
         * If:
         *  1. The installed version is less recent than the expected version
         *  2. The installed version version match the versionRange for the upgrade spec
         */
        if (semverLessThan(installedVersion, minimumVersion) && satisfies(installedVersion, versionRange)) {
          /**
           * If the minimum version would be compatible with the current version specified in the package.json
           * then we can do an `npm upgrade package`.
           */
          if (satisfies(minimumVersion, currentVersion)) {
            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.NpmUpgrade,
                properties: {
                  packageName,
                  versionRange,
                  minimumVersion,
                  currentVersion,
                },
              },
            });

            upgrades.push(packageName);
          } else {
            // send an analytic event for now but ideally we do an npm install package@version
            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.NpmUpgradeNotPossible,
                properties: {
                  packageName,
                  versionRange,
                  minimumVersion,
                  currentVersion,
                },
              },
            });
          }
        }
      }
    }

    return upgrades;
  } catch (error) {
    logger.error(error);

    return [];
  }
}

function getSpecifiedVersion(packageJson: z.infer<typeof packageJsonSchema>, packageName: string) {
  if (packageJson.dependencies && packageName in packageJson.dependencies) {
    return validRange(packageJson.dependencies[packageName]);
  }

  if (packageJson.devDependencies && packageName in packageJson.devDependencies) {
    return validRange(packageJson.devDependencies[packageName]);
  }

  return undefined;
}

const packageJsonSchema = z.object({
  dependencies: z.record(z.string(), z.string()).optional(),
  devDependencies: z.record(z.string(), z.string()).optional(),

  // we ignore peerDependencies & optionalDependencies are ignored
});

const packageLockSchema = z.object({
  packages: z.record(
    z.string(),
    z.object({
      version: z.string(),
    }),
  ),
});
